'use client';

import { useCallback, FC, useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { $ModalStore } from '@nx-frontend/shared/ui/modal';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { axiosClient } from '@nx-frontend/shared/api';
import { TemplatePagePayload, PopupPayload, PopupType } from '@nx-frontend/shared/types';
import { Template, PageWidgetActionType } from '@nx-frontend/shared/types';

const getTemplatePagePopupPayload = (payload: PopupPayload | undefined): TemplatePagePayload | null => {
  if (payload && payload.payloadType === PopupType.TEMPLATE_PAGE) {
    return payload;
  }
  return null;
};

export const TemplatePagePopup: FC = () => {
  const [store] = useUnit([$ModalStore]);
  const modal = store.find((modal) => modal.payload && modal.payload.payloadType === PopupType.TEMPLATE_PAGE);
  const payload = getTemplatePagePopupPayload(modal?.payload);

  const [pagesTemplates, setPagesTemplates] = useState<Template[]>([]);

  const t = useTranslations('webnotes');

  useEffect(() => {
    const fetchData = async () => {
      const res = await axiosClient.get(`/v2/api/templates`);
      if (res.data) {
        setPagesTemplates(res.data);
      }
    };
    fetchData();
  }, []);

  const createBlankPage = useCallback(
    (action: PageWidgetActionType) => {
      localStorage.setItem('blankPageHaveBeenCreated', 'true');
      if (action !== 'click' && pagesTemplates.length > 0) {
        payload?.onApply(action, pagesTemplates);
      } else {
        payload?.onApply(action);
      }
    },
    [pagesTemplates, payload]
  );

  return (
    <div
      data-cy="page-template-popup"
      className={classNames('w-[967px] dashSm:w-[400px]  h-[608px] px-[41px] pt-[8px] pb-[22px]', {
        'pb-[0px] h-[526px]': window.innerHeight < 720,
      })}
    >
      <div
        className={classNames('flex flex-col items-start gap-[64px] w-full', {
          'gap-[34px]': window.innerHeight < 720,
        })}
      >
        <div
          className={classNames('flex flex-col items-center gap-[48px] w-full', {
            'gap-[34px]': window.innerHeight < 720,
          })}
        >
          <div className="flex flex-col items-center gap-[2px] w-full">
            <div className="text-neutral-800 text-center text-[34px] font-bold h-[46px] dashSm:text-[24px] dashSm:h-full">
              {t('webnotes-choose_template')}
            </div>
            <div className="text-black text-center  text-4 ">{t('webnotes-template_popup_desc')}</div>
          </div>
          {pagesTemplates.length ? (
            <div className="flex items-start gap-[24px] w-full flex-wrap dashSm:h-[344px] dashSm:overflow-auto dashSm:items-center">
              <div
                data-cy="page-template-card"
                className="flex w-[279px] h-[344px] p-[24px] flex-col items-start gap-[32px] rounded-[8px] border-[1px] border-solid border-[#EAEAEA] hover:border-[#176b87] transition-all duration-[200ms] ease-in-out bg-[#F7F7F7] cursor-pointer"
                onClick={() => createBlankPage('client-brief')}
              >
                <div className="flex flex-col items-start gap-[4px] self-stretch w-full">
                  <div data-cy="page-template-title" className="text-neutral-800 text-4 font-semibold ">
                    {t('webnotes-client_brief')}
                  </div>
                  <div className="w-full text-neutral-800 text-[14px] ">{t('webnotes-client_brief_template_desc')}</div>
                </div>
                <div className="w-[231px] h-full">
                  <img src="/assets/img/page-widget-template-1.png" width={231} height={203} alt="" />
                </div>
              </div>

              <div
                data-cy="page-template-card"
                className="flex w-[279px] h-[344px] p-[24px] flex-col items-start gap-[32px] rounded-[8px] border-[1px] border-solid border-[#EAEAEA] bg-[#F7F7F7] cursor-pointer hover:border-[#176b87] transition-all duration-[200ms] ease-in-out "
                onClick={() => createBlankPage('customer-testimonials')}
              >
                <div className="flex flex-col items-start gap-[4px] self-stretch w-full">
                  <div data-cy="page-template-title" className="text-neutral-800 text-4 font-semibold ">
                    {t('webnotes-customer_testimonials')}
                  </div>
                  <div className="w-full text-neutral-800 text-[14px] ">
                    {t('webnotes-customer_testimonials_template_desc')}
                  </div>
                </div>
                <div className="w-[231px] h-full">
                  <img src="/assets/img/page-widget-template-2.png" width={231} height={203} alt="" />
                </div>
              </div>

              <div
                data-cy="page-template-card"
                className="flex w-[279px] h-[344px] p-[24px] flex-col items-start gap-[32px] rounded-[8px] border-[1px] border-solid border-[#EAEAEA] bg-[#F7F7F7] cursor-pointer hover:border-[#176b87] transition-all duration-[200ms] ease-in-out "
                onClick={() => createBlankPage('competitor-analysis')}
              >
                <div className="flex flex-col items-start gap-[4px] self-stretch w-full">
                  <div data-cy="page-template-title" className="text-neutral-800 text-4 font-semibold ">
                    {t('webnotes-competitor_analysis')}
                  </div>
                  <div className="w-full text-neutral-800 text-[14px] ">
                    {t('webnotes-competitor_analysis_template_desc')}
                  </div>
                </div>
                <div className="w-[231px] h-full">
                  <img src="/assets/img/page-widget-template-3.png" width={231} height={203} alt="" />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex w-full h-[344px] justify-center items-center">
              <Spinner />
            </div>
          )}
        </div>
        <button
          data-cy="create-blank-page"
          className="text-[#176B87] text-[13px] font-bold"
          onClick={() => createBlankPage('click')}
        >
          {t('webnotes-start_with_blank_page')}
        </button>
      </div>
    </div>
  );
};
